import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';
import Nav from '../components/nav';
import SEO from '../components/SEO';

// ── Inject spinner keyframe once at module level ──────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('display-project-styles')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'display-project-styles';
  styleEl.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(styleEl);
}

// ── Role detection ────────────────────────────────────────────────────────────
// Returns: 'production_house' | 'film_enthusiast' | 'marketing_enthusiast' | 'guest'
// ⚠️ Check your login handler — update the localStorage key if different
const detectUserRole = () => {
  try {
    // ✅ Derive role from redirect_url stored at login
    const dashboard = localStorage.getItem('user_dashboard');
    if (dashboard) {
      if (dashboard.includes('film-enthusiast'))   return 'film_enthusiast';
      if (dashboard.includes('production-house'))  return 'production_house';
      if (dashboard.includes('marketing'))         return 'marketing_enthusiast';
    }

    // Fallback: check direct keys
    const role = localStorage.getItem('user_role');
    if (role) return role;

    // Fallback: decode JWT
    const token = localStorage.getItem('access_token');
    if (!token) return 'guest';
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || payload.user_type || payload.userType || 'guest';

  } catch {
    return 'guest';
  }
};

// ── Role context hint ──────────────────────────────────────────────────────────
const ROLE_HINTS = {
  production_house:     { icon: '🎬', text: "You're viewing as a Production House. Learn from this project or head back to your dashboard." },
  film_enthusiast:      { icon: '🎭', text: "You're a Film Enthusiast. Check out open roles and apply to be part of this project!" },
  marketing_enthusiast: { icon: '📣', text: "You're a Marketing Enthusiast. Connect with the team and offer your marketing expertise!" },
  guest:                { icon: '👋', text: 'Join ProducerBuddy to apply for roles, collaborate, and be part of exciting film projects.' },
};

const RoleHint = ({ role }) => {
  const hint = ROLE_HINTS[role] || ROLE_HINTS.guest;
  return (
    <div style={{
      background: '#0f2027', border: '1px solid #1f3a2a',
      borderRadius: 10, padding: '12px 16px',
      display: 'flex', gap: 10, alignItems: 'flex-start',
      marginBottom: 24,
    }}>
      <span style={{ fontSize: 20 }}>{hint.icon}</span>
      <p style={{ margin: 0, fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>{hint.text}</p>
    </div>
  );
};

// ── Role-based CTAs ───────────────────────────────────────────────────────────
const RoleActions = ({ role, ad, navigate }) => {
  const btnBase = {
    padding: '12px 28px', borderRadius: 50, fontWeight: 700,
    fontSize: 14, cursor: 'pointer', border: 'none',
    letterSpacing: 0.4, transition: 'all 0.2s',
  };

  if (role === 'production_house') return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button
        style={{ ...btnBase, background: '#008080', color: '#fff' }}
        onClick={() => navigate('/production-house/dashboard')}
      >
        🎬 Go to Your Dashboard
      </button>
    </div>
  );

  if (role === 'film_enthusiast') return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button
        style={{ ...btnBase, background: '#008080', color: '#fff' }}
        onClick={() => navigate(`/jobs?project=${ad.id}`)}
      >
        🎭 View Open Roles
      </button>
      <button
        style={{ ...btnBase, background: '#facc15', color: '#000' }}
        onClick={() => navigate(`/jobs?project=${ad.id}&apply=true`)}
      >
        ✍️ Apply Now
      </button>
      <button
        style={{ ...btnBase, background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151' }}
        onClick={() => navigate('/comunity')}
      >
        💬 Discuss in Community
      </button>
    </div>
  );

  if (role === 'marketing_enthusiast') return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button
        style={{ ...btnBase, background: '#008080', color: '#fff' }}
        onClick={() => navigate(`/enthusiast/dashboard?tab=connect&project=${ad.id}`)}
      >
        🤝 Collaborate with Team
      </button>
      <button
        style={{ ...btnBase, background: '#facc15', color: '#000' }}
        onClick={() => navigate('/enthusiast/dashboard?tab=opportunities')}
      >
        📣 Offer Marketing Services
      </button>
    </div>
  );

  // Guest
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <button
        style={{ ...btnBase, background: '#008080', color: '#fff' }}
        onClick={() => navigate('/login')}
      >
        🔑 Login to Apply
      </button>
      <button
        style={{ ...btnBase, background: '#1f2937', color: '#e5e7eb', border: '1px solid #374151' }}
        onClick={() => navigate('/signup')}
      >
        🚀 Join ProducerBuddy
      </button>
    </div>
  );
};

// ── Smart Text Renderer ───────────────────────────────────────────────────────
// Handles ANY user-pasted content: headings, bullets, links, emails, spacing
const SmartTextRenderer = ({ text }) => {
  if (!text) return null;

  const isHeading = (line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.length <= 3) return false;
    if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) return false;
    if (!isNaN(trimmed)) return false;
    // ALL CAPS line = heading
    return trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed);
  };

  const isBullet = (line) => {
    const trimmed = line.trim();
    return trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*');
  };

  const renderInlineLinks = (line) => {
    // Split by emails and URLs and render them as clickable links
    const tokenRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(https?:\/\/[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = tokenRegex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      const token = match[0];
      if (token.includes('@')) {
        parts.push(
          <a
            key={match.index}
            href={`mailto:${token}`}
            style={{ color: '#2dd4bf', textDecoration: 'underline', wordBreak: 'break-all' }}
          >
            {token}
          </a>
        );
      } else {
        parts.push(
          <a
            key={match.index}
            href={token}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2dd4bf', textDecoration: 'underline', wordBreak: 'break-all' }}
          >
            {token}
          </a>
        );
      }
      lastIndex = match.index + token.length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    return parts.length > 0 ? parts : line;
  };

  const lines = text.split('\n');

  return (
    <div style={{ lineHeight: 1.85, maxWidth: 680 }}>
      {lines.map((line, index) => {
        // Empty line — vertical spacing
        if (line.trim() === '') {
          return <div key={index} style={{ height: '0.6rem' }} />;
        }

        // ALL CAPS heading
        if (isHeading(line)) {
          return (
            <p
              key={index}
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#2dd4bf',
                margin: '1.5rem 0 0.5rem',
                paddingBottom: '0.35rem',
                borderBottom: '1px solid rgba(45,212,191,0.15)',
              }}
            >
              {line.trim()}
            </p>
          );
        }

        // Bullet point
        if (isBullet(line)) {
          const content = line.trim().replace(/^[•\-*]\s*/, '');
          return (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                margin: '0.35rem 0',
              }}
            >
              <span
                style={{
                  color: '#2dd4bf',
                  flexShrink: 0,
                  marginTop: 3,
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                •
              </span>
              <span
                style={{
                  fontSize: 16,
                  lineHeight: 1.75,
                  color: '#d1d5db',
                }}
              >
                {renderInlineLinks(content)}
              </span>
            </div>
          );
        }

        // Normal paragraph line
        return (
          <p
            key={index}
            style={{
              margin: '0.2rem 0',
              fontSize: 16,
              lineHeight: 1.8,
              color: '#d1d5db',
            }}
          >
            {renderInlineLinks(line)}
          </p>
        );
      })}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const DisplayProject = () => {
  const { ad_id } = useParams();
  const navigate  = useNavigate();
  const [ad, setAd]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [imgLoaded, setImgLoaded] = useState(false);
  const role = detectUserRole();

  useEffect(() => {
    const fetchAd = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${baseUrl}/feature-project/${ad_id}`);
        if (!res.ok) throw new Error('Project not found');
        setAd(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (ad_id) fetchAd();
  }, [ad_id]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <>
      <Nav />
      <div style={{
        minHeight: '100vh', background: '#0a0f1e',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center', color: '#e5e7eb' }}>
          <div style={{
            width: 48, height: 48, border: '3px solid #374151',
            borderTop: '3px solid #008080', borderRadius: '50%',
            animation: 'spin 1s linear infinite', margin: '0 auto 16px',
          }} />
          <p style={{ color: '#9ca3af' }}>Loading project...</p>
        </div>
      </div>
    </>
  );

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error || !ad) return (
    <>
      <Nav />
      <div style={{
        minHeight: '100vh', background: '#0a0f1e',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 16, color: '#e5e7eb',
      }}>
        <div style={{ fontSize: 64 }}>🎬</div>
        <h2 style={{ fontWeight: 700 }}>Project Not Found</h2>
        <p style={{ color: '#9ca3af' }}>{error || 'This featured project may have expired.'}</p>
        <button
          onClick={() => navigate('/')}
          style={{
            background: '#008080', color: '#fff', border: 'none',
            borderRadius: 50, padding: '12px 28px', fontWeight: 700, cursor: 'pointer',
          }}
        >
          ← Back to Home
        </button>
      </div>
    </>
  );

  return (
    <>
      <SEO
        title="Film Project"
        description="View film project details, production updates, and team information on ProducerBuddy — the all-in-one film production platform."
        url="https://producerbuddy.pilomation.com/display-project"
      />
      <Nav />
      <div style={{ minHeight: '100vh', background: '#0a0f1e', color: '#e5e7eb' }}>

        {/* ── Hero Image ─────────────────────────────────────────────────── */}
        <div style={{ position: 'relative', width: '100%', height: 480, overflow: 'hidden' }}>
          {/* Gradient overlay */}
          <div style={{
            position: 'absolute', inset: 0, zIndex: 2,
            background: 'linear-gradient(to bottom, rgba(10,15,30,0.2) 0%, rgba(10,15,30,0.7) 70%, #0a0f1e 100%)',
          }} />

          {/* Image */}
          {ad.img ? (
            <img
              src={ad.img}
              alt={ad.title}
              onLoad={() => setImgLoaded(true)}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                opacity: imgLoaded ? 1 : 0,
                transition: 'opacity 0.6s ease',
              }}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              background: 'linear-gradient(135deg, #0f4c3a, #0a0f1e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 80,
            }}>🎬</div>
          )}

          {/* Featured badge */}
          <div style={{
            position: 'absolute', top: 24, left: 24, zIndex: 3,
            background: 'rgba(0,128,128,0.9)', backdropFilter: 'blur(8px)',
            padding: '6px 16px', borderRadius: 20,
            fontSize: 11, fontWeight: 800, letterSpacing: 1,
            color: '#fff', textTransform: 'uppercase',
          }}>
            ⭐ Featured Project
          </div>

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            style={{
              position: 'absolute', top: 24, right: 24, zIndex: 3,
              background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)', color: '#e5e7eb',
              borderRadius: 50, padding: '8px 18px', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
            }}
          >
            ← Back
          </button>
        </div>

        {/* ── Content ───────────────────────────────────────────────────── */}
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '40px 24px 80px' }}>

          {/* Title */}
          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 48px)',
            fontWeight: 900, lineHeight: 1.15,
            marginBottom: 20, letterSpacing: -0.5,
            background: 'linear-gradient(135deg, #fff 60%, #34d399)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {ad.title}
          </h1>

          {/* Accent divider */}
          <div style={{
            width: 64, height: 3, borderRadius: 2,
            background: 'linear-gradient(to right, #008080, #34d399)',
            marginBottom: 28,
          }} />

          {/* ── Description — SmartTextRenderer ── */}
          {ad.description && (
            <div style={{ marginBottom: 40 }}>
              <SmartTextRenderer text={ad.description} />
            </div>
          )}

          {/* Role hint */}
          <RoleHint role={role} />

          {/* CTA Buttons */}
          <div style={{ marginTop: 16 }}>
            <RoleActions role={role} ad={ad} navigate={navigate} />
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #1f2937', margin: '48px 0 32px' }} />

          {/* Meta */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', color: '#6b7280', fontSize: 12 }}>
            <span>🏷️ Featured Promotion</span>
            <span>🎬 Film Production</span>
            <span>🌐 ProducerBuddy Platform</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default DisplayProject;